import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useIsOfferLocked } from '@/hooks/use-is-offer-locked';
import {
  DealItem,
  DealItemWithVersions,
} from '@/lib/graphql/generated/graphql';
import { useSetColumnSort } from '@/states/column-sort.state';
import { Row } from '@tanstack/react-table';
import { InfoIcon, Loader2 } from 'lucide-react';

import { VatRateCell } from '../cells/vat-rate-cell';
import { ExtendedColumnDef } from '../table-products';
import { SortIcon } from './sort';
import { TableCellWithRecentlyModified } from './table-cell-with-recently-modified';

interface ColumnProps {
  handleVatMargin: (id: string) => void;
  /** pour le surlignage/edit in-place des cellules */
  handleCellClick: (id: string, field: keyof DealItem) => void;

  /** Met à jour en local (et prépare la mutation) un champ K de DealItem */
  handleCellEdit: <K extends keyof DealItem>(
    id: string | undefined,
    field: K,
    value: DealItem[K],
    isLeafRow?: boolean
  ) => Promise<void>;

  /** Termine l’édition (Escape annule l’édition) */
  /** gestion du clavier (Échap / Entrée) */
  handleKeyDown: React.KeyboardEventHandler<HTMLInputElement>;
  /** gestion du blur (perte de focus) */
  handleBlur: React.FocusEventHandler<HTMLInputElement>;
}

const useColumnDefVatRate = ({
  handleVatMargin,
}: ColumnProps): ExtendedColumnDef<DealItemWithVersions> => {
  const { t } = useTranslation();
  const isOfferLocked = useIsOfferLocked();
  const { setColumnSort } = useSetColumnSort();

  const accessorKey: keyof DealItem = 'vatRate';

  const getValue = (row: Row<DealItemWithVersions>) => {
    return row.original.activeVersion.vatRate;
  };

  const getParentValue = (row: Row<DealItemWithVersions>) => {
    const subRowsValues: number[] = [];

    row.subRows.forEach((subRow) => {
      const value = subRow.original.activeVersion.vatRate;
      if (typeof value !== 'undefined' && value !== null) {
        subRowsValues.push(value);
      }
    });

    if (subRowsValues.length === 0) {
      return null;
    }

    const firstValue = subRowsValues[0];
    const allSame = subRowsValues.every((value) => value === firstValue);

    if (allSame) {
      return firstValue;
    }

    return '≠';
  };

  return useMemo(
    () => ({
      accessorKey: accessorKey,
      name: 'VatRate',
      accessorFn: (row) => row.activeVersion?.vatRate ?? 0,
      aggregationFn: 'mean',
      sortingFn: 'basic',
      header: () => (
        <div className="cursor-pointer font-semibold">
          <div className="flex items-center gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <InfoIcon className="h-4 w-4" />
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>{t('kpi.vat_tooltip')}</p>
              </TooltipContent>
            </Tooltip>{' '}
            <div
              className="flex flex-1 items-center gap-2"
              onClick={() => setColumnSort(accessorKey)}
            >
              {t('kpi.vat')}
              <SortIcon pointer="vatRate" />
            </div>
          </div>
        </div>
      ),
      cell: ({ row }) => {
        const isParent = row.subRows.length > 1;
        const displayValue = isParent ? getParentValue(row) : getValue(row);

        const isLoading =
          row.original.activeVersion.updatingFields.includes('vatRate');

        if (isLoading) {
          return (
            <div className="flex w-full items-center justify-center">
              <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
            </div>
          );
        }

        return (
          <TableCellWithRecentlyModified
            productId={row.original.id}
            field="vatRate"
            isParent={isParent}
          >
            {(isRecentlyModified) => (
              <VatRateCell
                dealItemId={row.original.id}
                displayValue={displayValue}
                hasVatMargin={row.original.activeVersion.hasVatMargin}
                isMarginChecked={
                  isParent
                    ? row.subRows.every(
                        (subRow) => subRow.original.activeVersion.hasVatMargin
                      )
                    : row.original.activeVersion.hasVatMargin
                }
                isParent={isParent}
                isOfferLocked={isOfferLocked}
                isRecentlyModified={isRecentlyModified}
                onToggleVatMargin={handleVatMargin}
              />
            )}
          </TableCellWithRecentlyModified>
        );
      },
      size: 180,
    }),
    [t, isOfferLocked, setColumnSort, handleVatMargin]
  );
};

export default useColumnDefVatRate;
